import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import {
    buildEmployeePieData,
    type OperationsByEmployeeData,
} from '../../domain/statisticsOperations';
import ChartPlaceholder from './ChartPlaceholder';

interface OperationsByEmployeeChartProps {
    readonly data: readonly OperationsByEmployeeData[];
    readonly loading?: boolean;
}

function OperationsByEmployeeChart({
    data,
    loading = false,
}: OperationsByEmployeeChartProps): JSX.Element {
    const { t } = useTranslation();

    const pieData = buildEmployeePieData(
        data,
        t('statistics.operations.unknownEmployee', 'Unknown'),
    );

    if (loading || pieData.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={pieData.length > 0} height={350} />;
    }

    const options: Highcharts.Options = {
        chart: {
            type: 'pie',
            height: 350,
        },
        title: {
            text: undefined,
        },
        tooltip: {
            pointFormat:
                '<b>{point.percentage:.1f}%</b><br/>' +
                t('statistics.operations.contracts', 'Contracts') +
                ': {point.contracts}<br/>' +
                t('statistics.operations.redemptions', 'Redemptions') +
                ': {point.redemptions}<br/>' +
                t('statistics.operations.prolongations', 'Prolongations') +
                ': {point.prolongations}<br/>' +
                t('statistics.operations.total', 'Total') +
                ': {point.y}',
        },
        plotOptions: {
            pie: {
                allowPointSelect: true,
                cursor: 'pointer',
                dataLabels: {
                    enabled: true,
                    format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                },
                showInLegend: true,
            },
        },
        series: [
            {
                type: 'pie',
                name: t('statistics.operations.total', 'Total operations'),
                data: pieData,
            },
        ],
        credits: {
            enabled: false,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default OperationsByEmployeeChart;
